import { fmt, useToast } from "@nabble/ui";
import { useState } from "react";
import { type AdminDataRequest, extendDataRequest, updateDataRequest } from "../../../api/adminDataRequests";
import { DETAILS_MAX, dataRequestError } from "../../../model/dataRequests";

/** What the drawer shows: the request alone, or the request with the answer or extension form. */
export type DrawerMode = "view" | "answer" | "extend";
export type Outcome = "DONE" | "REJECTED";

export interface RequestDrawerState {
  mode: DrawerMode;
  startMode: (next: DrawerMode) => void;
  outcome: Outcome;
  setOutcome: (outcome: Outcome) => void;
  text: string;
  setText: (text: string) => void;
  textError: string | null;
  error: string | null;
  busy: boolean;
  confirming: boolean;
  cancelConfirm: () => void;
  /** Checks the answer and, when it is written, asks for confirmation before sending it. */
  confirmAnswer: () => void;
  answer: () => Promise<void>;
  extend: () => Promise<void>;
  markInProgress: () => Promise<void>;
}

/** The state and actions of the drawer where an administrator handles one data request. */
export function useRequestDrawer(r: AdminDataRequest, onChanged: () => void): RequestDrawerState {
  const { show } = useToast();
  const [mode, setMode] = useState<DrawerMode>("view");
  const [outcome, setOutcome] = useState<Outcome>("DONE");
  const [text, setText] = useState("");
  const [textError, setTextError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  function startMode(next: DrawerMode): void {
    setMode(next);
    setText("");
    setTextError(null);
    setError(null);
  }

  function validText(missing: string): string | null {
    const value = text.trim();
    if (!value) {
      setTextError(missing);
      return null;
    }
    if (value.length > DETAILS_MAX) {
      setTextError(`Keep it under ${fmt.number(DETAILS_MAX)} characters.`);
      return null;
    }
    setTextError(null);
    return value;
  }

  async function run(action: () => Promise<void>, success: string, fallback: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      show({ title: success, tone: "success" });
      onChanged();
      return true;
    } catch (err) {
      setError(dataRequestError(err, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function markInProgress(): Promise<void> {
    await run(() => updateDataRequest(r.id, "IN_PROGRESS", ""), "Marked as in progress", "We couldn't update the request.");
  }

  function confirmAnswer(): void {
    if (validText("Write the answer the person will receive.") !== null) setConfirming(true);
  }

  async function answer(): Promise<void> {
    const value = text.trim();
    const ok = await run(() => updateDataRequest(r.id, outcome, value), "Answer sent", "We couldn't send the answer. Please try again.");
    setConfirming(false);
    if (ok) startMode("view");
  }

  async function extend(): Promise<void> {
    const value = validText("Explain why you need more time.");
    if (value === null) return;
    const ok = await run(() => extendDataRequest(r.id, value), "Deadline extended by 30 days", "We couldn't extend the deadline.");
    if (ok) startMode("view");
  }

  return {
    mode,
    startMode,
    outcome,
    setOutcome,
    text,
    setText,
    textError,
    error,
    busy,
    confirming,
    cancelConfirm: () => setConfirming(false),
    confirmAnswer,
    answer,
    extend,
    markInProgress,
  };
}
